import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchSubscriptionPlans,
  fetchPromotions,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
} from "@/api/subscription-plans";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Check,
  Trash,
  Save,
  ArrowLeft,
  Plus,
  Info,
  Percent,
  DollarSign,
  Sparkles,
} from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { SubscriptionPlan } from "@/types/subscription";
import {
  formatDuration,
  getMonthlyEquivalent,
  getPlanDiscountSavings,
  calculateFinalPrice,
  getDailyEquivalent,
} from "@/api/subscription-plans";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import PricingConfigurator from "@/components/subscription_plans/PricingConfigurator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

// Helper function to format currency based on currency code
const formatPriceWithCurrency = (
  amount: number,
  currencyCode: string
): string => {
  const currencyMap: Record<string, string> = {
    INR: "₹",
    USD: "$",
    MMK: "K",
  };

  const currency = currencyMap[currencyCode] || currencyMap.INR;
  return `${currency}${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const defaultPlan: SubscriptionPlan = {
  id: 0,
  name: "",
  description: null,
  duration: 1,
  durationType: "months",
  productId: "",
  isActive: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  priceConfigurations: [],
  planDiscountPercentage: 0,
};

const SubscriptionPlanForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNewPlan = !id || id === "new";
  const pageTitle = isNewPlan
    ? "Add Subscription Plan"
    : "Edit Subscription Plan";

  const [plan, setPlan] = useState<SubscriptionPlan>(defaultPlan);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: plans, isLoading } = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: fetchSubscriptionPlans,
  });

  useEffect(() => {
    if (plans && !isNewPlan && id) {
      const foundPlan = plans.find((p) => p.id.toString() === id);
      if (foundPlan) {
        // Ensure planDiscountPercentage exists
        setPlan({
          ...foundPlan,
          planDiscountPercentage: foundPlan.planDiscountPercentage || 0,
        });
      }
    }
  }, [plans, isNewPlan, id]);

  const handleInputChange = (
    field: keyof SubscriptionPlan,
    value: string | number | boolean
  ) => {
    setPlan((prev) => ({ ...prev, [field]: value }));
  };


  // Create plan mutation
  const createPlanMutation = useMutation({
    mutationFn: createSubscriptionPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionPlans"] });
      toast({
        title: "Plan created",
        description: `"${plan.name}" has been created successfully.`,
      });
      navigate("/admin/subscription-plans");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update plan mutation
  const updatePlanMutation = useMutation({
    mutationFn: (params: { id: number; data: Partial<SubscriptionPlan> }) =>
      updateSubscriptionPlan(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionPlans"] });
      toast({
        title: "Plan updated",
        description: `"${plan.name}" has been updated successfully.`,
      });
      navigate("/admin/subscription-plans");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete plan mutation
  const deletePlanMutation = useMutation({
    mutationFn: deleteSubscriptionPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionPlans"] });
      toast({
        title: "Plan deleted",
        description: `"${plan.name}" has been deleted.`,
        variant: "destructive",
      });
      navigate("/admin/subscription-plans");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const savePlan = async () => {
    try {
      setIsSaving(true);

      if (isNewPlan) {
        // Create new plan
        await createPlanMutation.mutateAsync({
          name: plan.name,
          description: plan.description,
          duration: plan.duration,
          durationType: plan.durationType,
          productId: plan.productId,
          isActive: plan.isActive,
          planDiscountPercentage: plan.planDiscountPercentage,
        });
      } else {
        // Update existing plan
        await updatePlanMutation.mutateAsync({
          id: plan.id,
          data: {
            name: plan.name,
            description: plan.description,
            duration: plan.duration,
            durationType: plan.durationType,
            productId: plan.productId,
            isActive: plan.isActive,
            planDiscountPercentage: plan.planDiscountPercentage,
          },
        });
      }
    } catch (error) {
      console.error("Error saving plan:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const deletePlan = async () => {
    if (!window.confirm(`Are you sure you want to delete "${plan.name}"?`)) {
      return;
    }

    try {
      setIsDeleting(true);
      await deletePlanMutation.mutateAsync(plan.id);
    } catch (error) {
      console.error("Error deleting plan:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading && !isNewPlan) {
    return (
      <AdminLayout title={pageTitle}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  // Find the price configuration based on currency priority: INR → USD → MMK
  const getPriceConfiguration = () => {
    if (!plan.priceConfigurations || plan.priceConfigurations.length === 0) {
      return { price: 0, currencyCode: "INR" };
    }

    // Filter active configurations first
    const activeConfigs = plan.priceConfigurations.filter((pc) =>
      "isActive" in pc ? pc.isActive : true
    );
    if (activeConfigs.length === 0) {
      return { price: 0, currencyCode: "INR" };
    }

    // Priority order: INR, USD, MMK
    const inrConfig = activeConfigs.find((pc) => pc.currencyCode === "INR");
    if (inrConfig) return inrConfig;

    const usdConfig = activeConfigs.find((pc) => pc.currencyCode === "USD");
    if (usdConfig) return usdConfig;

    const mmkConfig = activeConfigs.find((pc) => pc.currencyCode === "MMK");
    if (mmkConfig) return mmkConfig;

    // If none of the preferred currencies, return the first active config
    return activeConfigs[0];
  };

  const priceConfig = getPriceConfiguration();
  const basePrice = priceConfig.price;
  const currencyCode = priceConfig.currencyCode || "INR";

  // Calculate values for display
  const formattedDuration = formatDuration(plan.duration, plan.durationType);
  const hasPlanDiscount = plan.planDiscountPercentage > 0;
  const planDiscountAmount = basePrice * (plan.planDiscountPercentage / 100);
  const finalPrice = basePrice - planDiscountAmount;
  const monthlyPrice = getMonthlyEquivalent(
    finalPrice,
    plan.duration,
    plan.durationType
  );
  const dailyPrice = getDailyEquivalent(
    finalPrice,
    plan.duration,
    plan.durationType
  );

  return (
    <AdminLayout title={pageTitle}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">{pageTitle}</h1>
        </div>

        <div className="flex gap-2">
          {!isNewPlan && (
            <Button
              variant="destructive"
              onClick={deletePlan}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Trash className="mr-2 h-4 w-4" />
              )}
              Delete
            </Button>
          )}
          <Button onClick={savePlan} disabled={isSaving}>
            {isSaving ? (
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save
          </Button>
        </div>
      </div>

      <Tabs defaultValue="basic" className="w-full mb-6">
        <TabsList>
          <TabsTrigger value="basic">Basic Details</TabsTrigger>
          <TabsTrigger value="pricing">Pricing Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Plan Details</CardTitle>
                  <CardDescription>
                    Configure the basic details of your subscription plan
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Plan Name</Label>
                    <Input
                      id="name"
                      value={plan.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="e.g. Basic, Premium, Pro"
                    />
                    <p className="text-xs text-muted-foreground">
                      A clear, descriptive name for your subscription plan
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={plan.description || ""}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      placeholder="Describe your plan"
                    />
                    <p className="text-xs text-muted-foreground">
                      A brief description of what this plan offers
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="duration">Duration</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              The length of the subscription period. This
                              determines how long customers will be subscribed
                              before renewal.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <Input
                        id="duration"
                        type="number"
                        min="1"
                        step="1"
                        value={plan.duration}
                        onChange={(e) =>
                          handleInputChange(
                            "duration",
                            parseInt(e.target.value) || 1
                          )
                        }
                        placeholder="Enter duration"
                      />
                      <p className="text-xs text-muted-foreground">
                        {formattedDuration} subscription
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="durationType">Duration Type</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              The unit of time for the subscription duration.
                              Choose between days, months, or years.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <select
                        id="durationType"
                        value={plan.durationType}
                        onChange={(e) =>
                          handleInputChange(
                            "durationType",
                            e.target.value as "days" | "months" | "years"
                          )
                        }
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="days">Days</option>
                        <option value="months">Months</option>
                        <option value="years">Years</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        Select the time unit for the subscription period
                      </p>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="planDiscountPercentage">
                          Permanent Discount (%)
                        </Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              Permanent percentage discount built into this
                              plan. Time-limited promotional discounts are
                              managed separately.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>

                    <div className="relative">
                      <Input
                        id="planDiscountPercentage"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={plan.planDiscountPercentage || 0}
                        onChange={(e) =>
                          handleInputChange(
                            "planDiscountPercentage",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        placeholder="Discount percentage"
                        className="pr-10"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        %
                      </div>
                    </div>

                    {plan.planDiscountPercentage > 0 && (
                      <p className="text-xs text-green-600 mt-2">
                        {plan.planDiscountPercentage}% discount will be
                        permanently applied to this plan
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="isActive">Active Status</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            Toggle whether this plan is available for new
                            subscriptions. Inactive plans will not be shown to
                            customers.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Switch
                      id="isActive"
                      checked={plan.isActive}
                      onCheckedChange={(checked) =>
                        handleInputChange("isActive", checked)
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Product Integration</CardTitle>
                  <CardDescription>
                    Configure the product ID for in-app purchase integration
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="productId">Product ID</Label>
                    <Input
                      id="productId"
                      value={plan.productId || ""}
                      onChange={(e) => handleInputChange("productId", e.target.value)}
                      placeholder="com.example.app.premium_monthly"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter the product identifier for App Store/Play Store integration
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Preview</CardTitle>
                  <CardDescription>
                    See how your plan will appear
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-4 space-y-4">
                    <div>
                      <h3 className="font-bold text-xl">
                        {plan.name || "Plan Name"}
                      </h3>

                      {plan.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {plan.description}
                        </p>
                      )}

                      <div className="bg-muted/10 p-3 rounded-lg space-y-3">
                        {currencyCode && (
                          <div className="text-xs font-medium text-muted-foreground">
                            Price in {currencyCode}
                          </div>
                        )}

                        <div className="flex flex-col gap-2">
                          {/* Original price with plan discount */}
                          <div className="flex items-center gap-2">
                            {hasPlanDiscount ? (
                              <>
                                <span className="text-sm line-through text-muted-foreground">
                                  {formatPriceWithCurrency(
                                    basePrice,
                                    currencyCode
                                  )}
                                </span>
                                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 border-none">
                                  <DollarSign className="h-3 w-3 mr-1" />
                                  Standard Price
                                </Badge>
                              </>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                Standard price:{" "}
                                {formatPriceWithCurrency(
                                  basePrice,
                                  currencyCode
                                )}
                              </span>
                            )}
                          </div>

                          {/* Price after plan discount */}
                          {hasPlanDiscount && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                {formatPriceWithCurrency(
                                  finalPrice,
                                  currencyCode
                                )}
                              </span>
                              <Badge
                                variant="outline"
                                className="bg-blue-50 text-blue-700 border-blue-200"
                              >
                                <Percent className="h-3 w-3 mr-1" />
                                Plan Discount: {plan.planDiscountPercentage}%
                                off
                              </Badge>
                            </div>
                          )}
                        </div>

                        {/* Final price display */}
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold">
                            {formatPriceWithCurrency(finalPrice, currencyCode)}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            /{formattedDuration}
                          </span>
                        </div>

                        {monthlyPrice > 0 && (
                          <p className="text-sm text-muted-foreground">
                            {formatPriceWithCurrency(
                              monthlyPrice,
                              currencyCode
                            )}
                            /month (
                            {formatPriceWithCurrency(dailyPrice, currencyCode)}
                            /day)
                          </p>
                        )}

                        {planDiscountAmount > 0 && (
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              variant="secondary"
                              className="bg-green-100 text-green-700"
                            >
                              <Sparkles className="h-3 w-3 mr-1" />
                              Total savings:{" "}
                              {formatPriceWithCurrency(
                                planDiscountAmount,
                                currencyCode
                              )}{" "}
                              (
                              {Math.round(
                                (planDiscountAmount / basePrice) * 100
                              )}
                              %)
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>

                    {plan.productId && (
                      <div className="pt-3 border-t">
                        <h4 className="text-sm font-medium mb-2">
                          Product Integration
                        </h4>
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">
                            Product ID: {plan.productId}
                          </span>
                        </div>
                      </div>
                    )}

                    <div>
                      <Button className="w-full" disabled>
                        Subscribe
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="pricing">
          {isNewPlan ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8 text-muted-foreground">
                  <p className="mb-2">
                    You need to save the plan first before configuring pricing
                    options.
                  </p>
                  <Button onClick={savePlan}>
                    <Save className="mr-2 h-4 w-4" /> Save Plan to Continue
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <PricingConfigurator
              planId={plan.id}
              basePrice={finalPrice}
              baseCurrency={currencyCode}
              onSave={() => {
                toast({
                  title: "Pricing updated",
                  description:
                    "The pricing configuration has been updated successfully.",
                });
              }}
            />
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default SubscriptionPlanForm;
